// Pointer-capture edge/corner resize. The px ghost glides under the pointer
// (driven imperatively so it tracks pixel-by-pixel without re-rendering the
// tile tree) while the real layout snaps cell-to-cell through `resizeItem`.
//
// Extras layered on the core gesture:
//   - double-press on a handle → fit-to-content (PointerEvent.detail is
//     Chromium-only on pointer events, so detection is manual: 350ms / ≤4px);
//   - Shift on a corner → aspect-locked resize (width-dominant, pixel aspect);
//   - pointer near the scroll parent's edge → rAF auto-scroll, with the scroll
//     displacement folded into the pointer delta (this gesture never goes
//     through dnd-kit, so unlike drags it must correct for scroll itself).

import * as React from 'react';

import { clamp } from "../../lib/utils.ts";
import {
  resizeItem,
  DEFAULT_MAX_H,
} from './grid-layout';
import { createEdgeAutoScroller } from './edge-auto-scroll';
import {
  getScrollParent,
  gridWidthPx,
  pitchX,
  pitchY,
  tileHeightPx,
  tileWidthPx,
} from './grid-geometry';
import type { GridGestureCore, ResizeDirection } from './grid-gesture';

// Double-press window for the fit-to-content trigger.
const DOUBLE_PRESS_MS = 350;
const DOUBLE_PRESS_SLOP_PX = 4;

export interface GridResizeOptions {
  /** The imperatively-driven px ghost element. */
  ghostEl: () => HTMLElement | null;
  /** One-shot fit-to-content commit (double-press on a handle). */
  fitToContent: (id: string, direction: ResizeDirection) => void;
}

export function useGridResize(
  core: GridGestureCore,
  options: GridResizeOptions,
): {
  beginResize: (
    id: string,
    direction: ResizeDirection,
    event: React.PointerEvent,
  ) => void;
} {
  const optionsRef = React.useRef(options);
  React.useLayoutEffect(() => {
    optionsRef.current = options;
  });

  // Unmount teardown (mirror of the marquee's): the active gesture's cleanup
  // must run even when the grid unmounts mid-resize. Set on gesture start,
  // nulled inside cleanup itself.
  const activeCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => () => activeCleanupRef.current?.(), []);

  const lastDownRef = React.useRef<{
    key: string;
    t: number;
    x: number;
    y: number;
  } | null>(null);

  const beginResize = React.useCallback(
    (id: string, direction: ResizeDirection, event: React.PointerEvent) => {
      if (!core.canManage) return;
      // Gesture exclusivity: one gesture at a time per grid — a handle press
      // must not replace an in-flight drag/marquee.
      if (core.gestureRef.current != null) return;
      // Height-locked tiles (derived-height containers) never resize
      // vertically — bail before the gesture starts for any n/s direction.
      if (
        core.constraintsFor(id).lockH &&
        (direction.includes('n') || direction.includes('s'))
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      // Manual double-press detection (the first press starts a resize that
      // ends harmlessly on pointerup with a null preview).
      const now = performance.now();
      const pressKey = `${id}:${direction}`;
      const prevDown = lastDownRef.current;
      if (
        prevDown &&
        prevDown.key === pressKey &&
        now - prevDown.t < DOUBLE_PRESS_MS &&
        Math.hypot(event.clientX - prevDown.x, event.clientY - prevDown.y) <=
          DOUBLE_PRESS_SLOP_PX
      ) {
        lastDownRef.current = null;
        optionsRef.current.fitToContent(id, direction);
        return;
      }
      lastDownRef.current = {
        key: pressKey,
        t: now,
        x: event.clientX,
        y: event.clientY,
      };

      const metrics = core.measure();
      if (!metrics) return;
      const start = core.layoutRef.current.find((i) => i.id === id);
      if (!start) return;

      const scratch = core.scratch.current;
      scratch.metrics = metrics;
      scratch.lastResize = null;
      // Deltas accumulate from a fixed origin (the drag/external discipline):
      // previews compute from this snapshot, and the shell's commit
      // reconciliation is the single place live-layout divergence resolves.
      scratch.startLayout = core.layoutRef.current;
      core.setGesture({
        id,
        mode: 'resize',
        origin: null,
        preview: null,
        area: null,
      });

      const startX = event.clientX;
      const startY = event.clientY;
      const { x: startCellX, y: startCellY, w: startW, h: startH } = start;
      const constraints = core.constraintsFor(id);
      const minW = constraints.minW ?? 1;
      const minH = constraints.minH ?? 1;
      const maxW = Math.min(constraints.maxW ?? core.columns, core.columns);
      const maxH = constraints.maxH ?? DEFAULT_MAX_H;

      const pX = pitchX(metrics);
      const pY = pitchY(metrics);
      const gridW = gridWidthPx(metrics, core.columns);
      // Anchored opposite edges (right for west drags, bottom for north).
      const rightEdge = startCellX + startW;
      const bottomEdge = startCellY + startH;
      const hasE = direction.includes('e');
      const hasW = direction.includes('w');
      const hasS = direction.includes('s');
      const hasN = direction.includes('n');
      const isCorner = direction.length === 2;
      // Pixel aspect (rows are fixed-height while columns are fluid, so a
      // cell-count aspect would be wrong).
      const startAspect =
        tileWidthPx(metrics, startW) / Math.max(1, tileHeightPx(metrics, startH));

      const startLeftPx = startCellX * pX;
      const startTopPx = startCellY * pY;
      const startWPx = tileWidthPx(metrics, startW);
      const startHPx = tileHeightPx(metrics, startH);

      const applyGhost = (l: number, t: number, w: number, h: number) => {
        const g = optionsRef.current.ghostEl();
        if (!g) return;
        g.style.left = `${l}px`;
        g.style.top = `${t}px`;
        g.style.width = `${w}px`;
        g.style.height = `${h}px`;
      };
      // Seed the ghost at the tile's current rect so it fades in correctly.
      applyGhost(startLeftPx, startTopPx, startWPx, startHPx);

      const target = event.currentTarget as HTMLElement;
      const pointerId = event.pointerId;
      try {
        target.setPointerCapture(pointerId);
      } catch {
        // pointer may already be released
      }

      // Auto-scroll state. The scroll parent's displacement since gesture
      // start is folded into the vertical pointer delta so cell math stays in
      // grid-content space while the viewport moves.
      const scrollParent = getScrollParent(core.gridEl());
      const startScrollTop = scrollParent?.scrollTop ?? 0;
      const lastPointer = { x: startX, y: startY, shift: event.shiftKey };

      const applyPointer = () => {
        const scrollDelta = (scrollParent?.scrollTop ?? 0) - startScrollTop;
        const rawDX = lastPointer.x - startX;
        const rawDY = lastPointer.y - startY + scrollDelta;
        const aspectLocked = lastPointer.shift && isCorner;

        // Ghost: raw, un-snapped extent clamped to the grid, updated every
        // pixel via the DOM so it glides under the snapping card.
        let gl = startLeftPx;
        let gt = startTopPx;
        let gw = startWPx;
        let gh = startHPx;
        if (hasE) gw += rawDX;
        else if (hasW) {
          gl += rawDX;
          gw -= rawDX;
        }
        if (aspectLocked) {
          // Width is the dominant axis; height glides on the locked aspect.
          gh = gw / startAspect;
          if (hasN) gt = startTopPx + startHPx - gh;
        } else if (hasS) {
          gh += rawDY;
        } else if (hasN) {
          gt += rawDY;
          gh -= rawDY;
        }
        if (gl < 0) {
          gw += gl;
          gl = 0;
        }
        if (gt < 0) {
          gh += gt;
          gt = 0;
        }
        gw = clamp(gw, metrics.colWidth, gridW - gl);
        gh = Math.max(gh, metrics.rowHeight);
        applyGhost(gl, gt, gw, gh);

        // Snapped rect: drives the real layout. Only reflow on cell changes.
        const stepX = Math.round(rawDX / pX);
        const stepY = Math.round(rawDY / pY);
        let nx = startCellX;
        let ny = startCellY;
        let nw = startW;
        let nh = startH;
        if (hasE) nw = startW + stepX;
        else if (hasW) {
          nx = clamp(startCellX + stepX, 0, rightEdge - minW);
          nw = rightEdge - nx;
        }
        if (aspectLocked) {
          // Derive the snapped height from the (constraint-clamped) snapped
          // width through the pixel aspect, releasing back to free resize the
          // moment Shift lifts (no sticky state — re-read per event).
          const effW = clamp(nw, minW, maxW);
          nh = clamp(
            Math.round((tileWidthPx(metrics, effW) / startAspect + metrics.gapY) / pY),
            minH,
            maxH,
          );
          if (hasN) ny = Math.max(0, bottomEdge - nh);
        } else if (hasS) {
          nh = startH + stepY;
        } else if (hasN) {
          ny = clamp(startCellY + stepY, 0, bottomEdge - minH);
          nh = bottomEdge - ny;
        }

        const last = scratch.lastResize;
        if (
          last &&
          last.x === nx &&
          last.y === ny &&
          last.w === nw &&
          last.h === nh
        ) {
          return;
        }
        scratch.lastResize = { x: nx, y: ny, w: nw, h: nh };
        core.setPreview(
          resizeItem(
            scratch.startLayout,
            id,
            nx,
            ny,
            nw,
            nh,
            core.columns,
            constraints,
            core.flow,
          ),
        );
      };

      // Edge auto-scroll: `onScrolled` re-runs the resize math so the tile
      // keeps growing while the pointer sits parked at the viewport edge.
      const scroller = createEdgeAutoScroller({
        scrollParent,
        onScrolled: applyPointer,
      });

      const onMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) return;
        lastPointer.x = move.clientX;
        lastPointer.y = move.clientY;
        lastPointer.shift = move.shiftKey;
        scroller.update(move.clientY);
        applyPointer();
      };

      const cleanup = () => {
        activeCleanupRef.current = null;
        scroller.stop();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onPointerCancel);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('blur', onCancel);
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          // capture may already be gone
        }
      };
      const onUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) return;
        cleanup();
        core.commitGesture();
      };
      // An OS-interrupted resize (pointercancel, window blur) or Escape must
      // NOT commit a half-done resize — cancel, mirroring the marquee's
      // listener set. Only cancel a gesture that is still ours.
      const onCancel = () => {
        cleanup();
        if (core.gestureRef.current?.mode === 'resize') core.cancelGesture();
      };
      const onPointerCancel = (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        onCancel();
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onCancel();
      };
      activeCleanupRef.current = cleanup;

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onPointerCancel);
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('blur', onCancel);
    },
    [core],
  );

  return React.useMemo(() => ({ beginResize }), [beginResize]);
}
